<script setup>
/**
 * Webhooks.
 *
 * Archetype E: the shell's header carries the title, the search box and the
 * `Add new webhook` button, and the body is nothing but the table. The paywall
 * still replaces the table wholesale on plans without API access - the header
 * drops its search and button in that case, because there is nothing to search
 * and nothing to add.
 */
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useBranding } from 'shared/composables/useBranding';
import { picoSearch } from '@scmmishra/pico-search';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import SettingsPageShell from 'dashboard/components-next/Settings/SettingsPageShell.vue';
import SettingsSection from 'dashboard/components-next/Settings/SettingsSection.vue';
import Skeleton from 'dashboard/components-next/skeleton/Skeleton.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ConfirmDialog from 'dashboard/components-next/dialog/ConfirmDialog.vue';
import SettingsSearchInput from 'dashboard/components-next/Settings/SettingsSearchInput.vue';
import { BaseTable } from 'dashboard/components-next/table';
import NewWebhook from './NewWebHook.vue';
import EditWebhook from './EditWebHook.vue';
import WebhookRow from './WebhookRow.vue';
import WebhookPaywall from './WebhookPaywall.vue';

const { t } = useI18n();
const store = useStore();
const { replaceInstallationName } = useBranding();

const records = useMapGetter('webhooks/getWebhooks');
const uiFlags = useMapGetter('webhooks/getUIFlags');
const accountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const isOnChatwootCloud = useMapGetter('globalConfig/isOnChatwootCloud');
const integration = useMapGetter('integrations/getIntegration');

const searchQuery = ref('');
const showAddPopup = ref(false);
const showEditPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const selectedWebHook = ref({});

const apiAndWebhooksEnabled = computed(
  () =>
    !isOnChatwootCloud.value ||
    isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.API_AND_WEBHOOKS
    )
);

const description = computed(() =>
  replaceInstallationName(integration.value('webhook').description || '')
);

const filteredRecords = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return records.value;
  return picoSearch(records.value, query, ['name', 'url']);
});

const tableHeaders = computed(() => [
  t('INTEGRATION_SETTINGS.WEBHOOK.LIST.TABLE_HEADER.WEBHOOK_ENDPOINT'),
  t('INTEGRATION_SETTINGS.WEBHOOK.LIST.TABLE_HEADER.ACTIONS'),
]);

const noDataMessage = computed(() =>
  searchQuery.value
    ? t('INTEGRATION_SETTINGS.WEBHOOK.NO_RESULTS')
    : t('INTEGRATION_SETTINGS.WEBHOOK.LIST.404')
);

const openAddPopup = () => {
  showAddPopup.value = true;
};

const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = webhook => {
  selectedWebHook.value = webhook;
  showEditPopup.value = true;
};

const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = webhook => {
  selectedWebHook.value = webhook;
  showDeleteConfirmationPopup.value = true;
};

const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteWebhook = async id => {
  try {
    await store.dispatch('webhooks/delete', id);
    useAlert(t('INTEGRATION_SETTINGS.WEBHOOK.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('INTEGRATION_SETTINGS.WEBHOOK.DELETE.API.ERROR_MESSAGE'));
  }
};

const confirmDeletion = () => {
  closeDeletePopup();
  deleteWebhook(selectedWebHook.value.id);
};

watch(
  apiAndWebhooksEnabled,
  enabled => {
    if (enabled) store.dispatch('webhooks/get');
  },
  { immediate: true }
);

onMounted(() => {
  store.dispatch('integrations/get', 'webhook');
});
</script>

<template>
  <SettingsPageShell
    wide
    :title="$t('SIDEBAR.WEBHOOKS')"
    :description="description"
  >
    <template v-if="apiAndWebhooksEnabled" #actions>
      <Button
        size="sm"
        :label="$t('INTEGRATION_SETTINGS.WEBHOOK.HEADER_BTN_TXT')"
        @click="openAddPopup"
      />
    </template>

    <template v-if="apiAndWebhooksEnabled" #tabs>
      <SettingsSearchInput
        v-model="searchQuery"
        :placeholder="$t('INTEGRATION_SETTINGS.WEBHOOK.SEARCH_PLACEHOLDER')"
      />
    </template>

    <WebhookPaywall v-if="!apiAndWebhooksEnabled" />

    <SettingsSection
      v-else-if="uiFlags.fetchingList"
      role="status"
      aria-busy="true"
      :aria-label="$t('INTEGRATION_SETTINGS.WEBHOOK.LOADING')"
    >
      <div
        v-for="row in 4"
        :key="row"
        class="flex items-center gap-3 px-4 py-4"
      >
        <Skeleton variant="circle" :announce="false" class="size-8" />
        <Skeleton variant="text" :lines="2" :announce="false" class="flex-1" />
      </div>
    </SettingsSection>

    <BaseTable
      v-else
      :headers="tableHeaders"
      :items="filteredRecords"
      :no-data-message="noDataMessage"
    >
      <template #row="{ items }">
        <WebhookRow
          v-for="(webHookItem, index) in items"
          :key="webHookItem.id"
          :index="index"
          :webhook="webHookItem"
          @edit="openEditPopup"
          @delete="openDeletePopup"
        />
      </template>
    </BaseTable>

    <woot-modal
      v-if="apiAndWebhooksEnabled"
      v-model:show="showAddPopup"
      :on-close="hideAddPopup"
    >
      <NewWebhook v-if="showAddPopup" :on-close="hideAddPopup" />
    </woot-modal>

    <woot-modal
      v-if="apiAndWebhooksEnabled"
      v-model:show="showEditPopup"
      :on-close="hideEditPopup"
    >
      <EditWebhook
        v-if="showEditPopup"
        :id="selectedWebHook.id"
        :value="selectedWebHook"
        :on-close="hideEditPopup"
      />
    </woot-modal>

    <ConfirmDialog
      v-if="apiAndWebhooksEnabled"
      v-model:show="showDeleteConfirmationPopup"
      :title="$t('INTEGRATION_SETTINGS.WEBHOOK.DELETE.CONFIRM.TITLE')"
      :description="
        $t('INTEGRATION_SETTINGS.WEBHOOK.DELETE.CONFIRM.MESSAGE', {
          webhookURL: selectedWebHook.url,
        })
      "
      :confirm-button-label="
        $t('INTEGRATION_SETTINGS.WEBHOOK.DELETE.CONFIRM.YES')
      "
      :cancel-button-label="
        $t('INTEGRATION_SETTINGS.WEBHOOK.DELETE.CONFIRM.NO')
      "
      @close="closeDeletePopup"
      @confirm="confirmDeletion"
    />
  </SettingsPageShell>
</template>
